import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
    ArrowLeft,
    ExternalLink,
    Building,
    MapPin,
    FileText,
    Download,
    Shield,
    Clock
} from 'lucide-react';
import { searchService, LEIRecord } from '../services/searchService';
import { louService, LOU } from '../services/louService';
import { formatLegalForm, formatEntityStatus } from '../utils/legalFormUtils';
import { CSV_HEADERS, extractRecordData, createExportRecord } from '../utils/exportUtils';
import ReactCountryFlag from 'react-country-flag';
import { trackLEIView, trackExport } from '../utils/analytics';

const RecordPage: React.FC = () => {
    const { lei } = useParams<{ lei: string }>();
    const location = useLocation();
    const [record, setRecord] = useState<LEIRecord | null>(null);
    const [lou, setLOU] = useState<LOU | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Extract search parameters from the current location state or referrer
    const getBackToSearchUrl = () => {
        // If we came from the search page, try to preserve the search state
        if (location.state?.fromSearch) {
            return `/search${location.state.searchQuery}`;
        }
        // Default back to search
        return '/search';
    };

    useEffect(() => {
        if (lei) {
            fetchRecord(lei);
        }
    }, [lei]);

    const fetchRecord = async (leiCode: string) => {
        try {
            setLoading(true);

            const data = await searchService.getRecord(leiCode);
            setRecord(data);

            // Fetch LOU information based on the lou field in the record
            try {
                if (data.lou) {
                    const louData = await louService.findLOUByLOUCode(data.lou);
                    setLOU(louData);
                }
            } catch (louError) {
                console.warn('Failed to fetch LOU data:', louError);
                // Don't fail the whole page if LOU data can't be loaded
            }

            // Track LEI record view
            trackLEIView(data.lei, data.registered_name);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch record');
        } finally {
            setLoading(false);
        }
    };

    const exportToCSV = () => {
        if (!record) return;

        // Track export event
        trackExport('csv', 1);

        // Use comprehensive headers and data extraction
        const data = extractRecordData(record);

        // Create CSV content with proper formatting
        const csvContent = [
            CSV_HEADERS.map((header) => `"${header}"`).join(','),
            data.map((field) => `"${field}"`).join(',')
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `lei_record_${record.lei}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportToJSON = () => {
        if (!record) return;

        // Track export event
        trackExport('json', 1);

        // Use comprehensive export structure for consistency with CSV
        const exportRecord = createExportRecord(record);
        const jsonContent = JSON.stringify(exportRecord, null, 2);

        const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `lei_record_${record.lei}.json`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Helper function to format dates
    const formatDate = (dateString?: string): string => {
        if (!dateString) return 'Not specified';

        try {
            const date = new Date(dateString);
            // Format as YYYY-MM-DD HH:mm:ss UTC
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString; // Return original string if parsing fails
        }
    };

    // Helper function to check if headquarters address exists
    const hasHeadquartersAddress = (): boolean => {
        if (!record) return false;
        return !!(
            record.headquarters_address_line_1 ||
            record.headquarters_address_line_2 ||
            record.headquarters_address_line_3 ||
            record.headquarters_address_line_4 ||
            record.headquarters_city ||
            record.headquarters_region ||
            record.headquarters_postal_code ||
            record.headquarters_country_code
        );
    };

    // Helper function to get address for display (headquarters with business fallback)
    const getDisplayAddress = (type: 'headquarters' | 'business') => {
        if (!record) return null;

        // For headquarters, use business address as fallback if headquarters is empty
        const useBusinessAsFallback = type === 'headquarters' && !hasHeadquartersAddress();

        const addressLines = useBusinessAsFallback
            ? [
                  record.legal_address_line_1,
                  record.legal_address_line_2,
                  record.legal_address_line_3,
                  record.legal_address_line_4
              ]
            : type === 'headquarters'
              ? [
                    record.headquarters_address_line_1,
                    record.headquarters_address_line_2,
                    record.headquarters_address_line_3,
                    record.headquarters_address_line_4
                ]
              : [
                    record.legal_address_line_1,
                    record.legal_address_line_2,
                    record.legal_address_line_3,
                    record.legal_address_line_4
                ];

        const city = useBusinessAsFallback
            ? record.legal_city
            : type === 'headquarters'
              ? record.headquarters_city
              : record.legal_city;
        const region = useBusinessAsFallback
            ? record.legal_region
            : type === 'headquarters'
              ? record.headquarters_region
              : record.legal_region;
        const postalCode = useBusinessAsFallback
            ? record.legal_postal_code
            : type === 'headquarters'
              ? record.headquarters_postal_code
              : record.legal_postal_code;
        const countryCode = useBusinessAsFallback
            ? record.legal_country_code
            : type === 'headquarters'
              ? record.headquarters_country_code
              : record.legal_country_code;

        // Check if any address data exists
        const hasAnyAddress =
            addressLines.some((line) => line) || city || region || postalCode || countryCode;
        if (!hasAnyAddress) return null;

        return {
            lines: addressLines.filter((line) => line),
            city,
            region,
            postalCode,
            countryCode,
            isFallback: useBusinessAsFallback
        };
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded-sm w-1/4 mb-6"></div>
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6 mb-6">
                            <div className="h-8 bg-gray-200 rounded-sm w-1/2 mb-4"></div>
                            <div className="h-4 bg-gray-200 rounded-sm w-1/3"></div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 space-y-6">
                                {[...Array(3)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="bg-white rounded-lg shadow-xs border border-gray-200 p-6"
                                    >
                                        <div className="h-6 bg-gray-200 rounded-sm w-1/3 mb-4"></div>
                                        <div className="space-y-3">
                                            {[...Array(4)].map((_, j) => (
                                                <div
                                                    key={j}
                                                    className="h-4 bg-gray-200 rounded-sm w-1/2"
                                                ></div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-6">
                                {[...Array(3)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="bg-white rounded-lg shadow-xs border border-gray-200 p-6"
                                    >
                                        <div className="h-6 bg-gray-200 rounded-sm w-1/2 mb-4"></div>
                                        <div className="space-y-3">
                                            {[...Array(3)].map((_, j) => (
                                                <div
                                                    key={j}
                                                    className="h-10 bg-gray-200 rounded-sm"
                                                ></div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !record) {
        return (
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link
                        to={getBackToSearchUrl()}
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors mb-6"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Search
                    </Link>

                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-12 text-center">
                        <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h1 className="text-xl font-semibold text-gray-900 mb-2">
                            {error ? 'Error Loading Record' : 'Record Not Found'}
                        </h1>
                        <p className="text-gray-500 mb-6">
                            {error || 'The requested LEI record could not be found.'}
                        </p>
                        <Link
                            to={getBackToSearchUrl()}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Return to Search
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back Button */}
                <div className="mb-6">
                    <Link
                        to={getBackToSearchUrl()}
                        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Search
                    </Link>
                </div>

                {/* Page Header */}
                <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6 mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                {record.registered_name}
                            </h1>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="font-mono bg-gray-100 px-2 py-1 rounded-sm">
                                    LEI: {record.lei}
                                </span>
                                <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        record.entity_status_code === 'ACTIVE'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-gray-100 text-gray-700'
                                    }`}
                                >
                                    {record.entity_status_code || 'UNKNOWN'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Record Details */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Information */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Entity Information */}
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Building className="h-6 w-6 text-primary-600" />
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Entity Information
                                </h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Legal Form</p>
                                    <p className="text-gray-900">
                                        {formatLegalForm(record.legal_form)}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-sm font-medium text-gray-500">
                                        Entity Status
                                    </p>
                                    <p className="text-gray-900">
                                        {formatEntityStatus(record.entity_status_code)}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-sm font-medium text-gray-500">
                                        Entity Category
                                    </p>
                                    <p className="text-gray-900">Not specified</p>
                                </div>

                                <div>
                                    <p className="text-sm font-medium text-gray-500">Entity Type</p>
                                    <p className="text-gray-900">Not specified</p>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="border-t border-gray-200 pt-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Other Entity Names
                                </h3>
                                {record.other_names && record.other_names.length > 0 ? (
                                    <div className="space-y-2">
                                        {record.other_names.map((name, index) => (
                                            <p key={index} className="text-gray-900">
                                                {name}
                                            </p>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500">No other names specified</p>
                                )}
                            </div>
                        </div>

                        {/* Registration Details */}
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <FileText className="h-6 w-6 text-primary-600" />
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Registration Details
                                </h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">
                                        Jurisdiction
                                    </p>
                                    <p className="text-gray-900 flex items-center gap-2">
                                        <ReactCountryFlag
                                            countryCode={record.registered_country_code || ''}
                                            svg
                                            style={{
                                                width: '16px',
                                                height: '12px'
                                            }}
                                            title={record.legal_jurisdiction}
                                        />
                                        <span>{record.legal_jurisdiction || 'Not specified'}</span>
                                    </p>
                                </div>

                                <div>
                                    <p className="text-sm font-medium text-gray-500">
                                        Business Registry Name
                                    </p>
                                    <p className="text-gray-900">
                                        {record.business_registry_name || 'Not specified'}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-sm font-medium text-gray-500">
                                        Registration Number
                                    </p>
                                    <p className="text-gray-900">
                                        {record.business_registry_identifier || 'Not specified'}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-sm font-medium text-gray-500">
                                        Business Registry Code
                                    </p>
                                    <p className="text-gray-900">
                                        {record.business_registry_code || 'Not specified'}
                                    </p>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="border-t border-gray-200 pt-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">
                                            Initial Registration
                                        </p>
                                        <p className="text-gray-900">Not specified</p>
                                    </div>

                                    <div>
                                        <p className="text-sm font-medium text-gray-500">
                                            LEI Registration
                                        </p>
                                        <p className="text-gray-900">
                                            {formatDate(record.lei_assignment_date)}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-sm font-medium text-gray-500">
                                            Last Update
                                        </p>
                                        <p className="text-gray-900">
                                            {formatDate(record.lei_record_last_update)}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-sm font-medium text-gray-500">
                                            Next Renewal
                                        </p>
                                        <p className="text-gray-900">
                                            {formatDate(record.lei_next_renewal_date)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Address Information */}
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <MapPin className="h-6 w-6 text-primary-600" />
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Address Information
                                </h2>
                            </div>

                            {/* Headquarters Address */}
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <p className="text-sm font-medium text-gray-500">
                                        Headquarters Address
                                    </p>
                                </div>
                                {(() => {
                                    const address = getDisplayAddress('headquarters');
                                    if (!address) {
                                        return <p className="text-gray-500">Not specified</p>;
                                    }
                                    return (
                                        <div className="text-gray-900 leading-relaxed">
                                            {address.lines.map((line, index) => (
                                                <div key={index}>{line}</div>
                                            ))}
                                            {address.city && <div>{address.city}</div>}
                                            {address.region && <div>{address.region}</div>}
                                            {address.postalCode && <div>{address.postalCode}</div>}
                                            {address.countryCode && (
                                                <div className="font-medium">
                                                    {address.countryCode}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Divider */}
                            <div className="border-t border-gray-200 pt-6">
                                <p className="text-sm font-medium text-gray-500 mb-3">
                                    Business Address
                                </p>
                                {(() => {
                                    const address = getDisplayAddress('business');
                                    if (!address) {
                                        return <p className="text-gray-500">Not specified</p>;
                                    }
                                    return (
                                        <div className="text-gray-900 leading-relaxed">
                                            {address.lines.map((line, index) => (
                                                <div key={index}>{line}</div>
                                            ))}
                                            {address.city && <div>{address.city}</div>}
                                            {address.region && <div>{address.region}</div>}
                                            {address.postalCode && <div>{address.postalCode}</div>}
                                            {address.countryCode && (
                                                <div className="font-medium">
                                                    {address.countryCode}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Quick Actions */}
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Quick Actions
                            </h3>
                            <div className="space-y-3">
                                <button
                                    onClick={exportToCSV}
                                    className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Download className="h-4 w-4" />
                                    Download CSV
                                </button>
                                <button
                                    onClick={exportToJSON}
                                    className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Download className="h-4 w-4" />
                                    Download JSON
                                </button>
                            </div>
                        </div>

                        {/* External Links */}
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                External Links
                            </h3>
                            <div className="space-y-3">
                                <a
                                    href={`https://www.gleif.org/lei/${record.lei}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    View on GLEIF
                                </a>
                                <a
                                    href={`https://opencorporates.com/companies?q=${encodeURIComponent(record.registered_name)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    OpenCorporates
                                </a>
                            </div>
                        </div>

                        {/* Data Quality */}
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Shield className="h-5 w-5 text-primary-600" />
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Data Quality
                                </h3>
                            </div>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Validation Sources</span>
                                    <span className="text-gray-900">
                                        {record.business_registry_name || 'Not specified'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Managing LOU</span>
                                    <div className="text-right">
                                        <span className="text-gray-900">
                                            {record.lou || 'Not specified'}
                                        </span>
                                        {lou && (
                                            <div className="mt-1">
                                                <Link
                                                    to={`/lou/${record.lou?.replace(/'/g, '')}`}
                                                    className="text-primary-600 hover:text-primary-800 font-medium hover:underline text-sm"
                                                >
                                                    {lou.name}
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Timeline */}
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Clock className="h-5 w-5 text-primary-600" />
                                <h3 className="text-lg font-semibold text-gray-900">Timeline</h3>
                            </div>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Entity Established</span>
                                    <span className="text-gray-900">Not specified</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">LEI Issued</span>
                                    <span className="text-gray-900">
                                        {formatDate(record.lei_assignment_date)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Record Updated</span>
                                    <span className="text-gray-900">
                                        {formatDate(record.lei_record_last_update)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecordPage;
